"use client";

import { useCallback, useMemo, useState } from "react";
import { useExploreRoute } from "../../_lib/explore-route-context";
import type {
  ExploreBriefCard,
  FeedDaySection,
  FeedPageData,
} from "../../types";
import { FeaturedStoryCard } from "../featured-story-card";
import { type BriefSurface, useBriefRowTracking } from "../use-brief-analytics";
import { BriefPreviewDialog } from "./brief-preview-dialog";
import { BriefStoriesOverlay } from "./briefs-stories-overlay";

export type BriefCardStyle = "compact" | "compact5" | "flat" | "sidebar-card";

// ─── Category dot colors (matching Explore compact variant) ──

const CATEGORY_DOT_COLORS: Record<string, string> = {
  "video-and-film": "var(--explore-accent-red)",
  "image-and-design": "var(--explore-accent-purple)",
  "audio-and-voice": "var(--explore-accent-orange)",
  "product-design": "var(--explore-accent-teal)",
  "vibe-coding": "var(--explore-accent-deal)",
  writing: "var(--explore-text-muted)",
  other: "var(--explore-text-muted)",
};

function getCategoryDotColor({ slug }: { slug: string }): string {
  return CATEGORY_DOT_COLORS[slug] ?? "var(--explore-text-muted)";
}

// ─── Brief row: Compact (A) — dot + title + time in card ─────

function CompactBriefRow({
  brief,
  surface,
  positionInList,
  onClick,
}: {
  brief: ExploreBriefCard;
  surface: BriefSurface;
  positionInList: number;
  onClick: () => void;
}) {
  const { ref, handleClick } = useBriefRowTracking({
    brief,
    surface,
    positionInList,
    onClick,
  });
  return (
    <button
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--explore-bg-highlight)]"
      onClick={handleClick}
      ref={ref}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{
          backgroundColor: getCategoryDotColor({
            slug: brief.categorySlug ?? "",
          }),
        }}
      />
      <p className="min-w-0 flex-1 font-medium text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.4] max-sm:line-clamp-2 sm:truncate">
        {brief.title}
      </p>
      <span className="shrink-0 text-[0.65rem] text-[var(--explore-text-muted)] tabular-nums">
        {brief.timeAgo}
      </span>
    </button>
  );
}

// ─── Brief row: Compact5 (B) — title + source + time ─────────

function Compact5BriefRow({
  brief,
  surface,
  positionInList,
  onClick,
}: {
  brief: ExploreBriefCard;
  surface: BriefSurface;
  positionInList: number;
  onClick: () => void;
}) {
  const { ref, handleClick } = useBriefRowTracking({
    brief,
    surface,
    positionInList,
    onClick,
  });
  const sourceLabel =
    brief.tags[0]?.displayName ?? brief.categoryDisplayName ?? "";

  return (
    <button
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--explore-bg-highlight)]"
      onClick={handleClick}
      ref={ref}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <p className="min-w-0 flex-1 font-medium text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.4] max-sm:line-clamp-2 sm:truncate">
        {brief.title}
      </p>
      {sourceLabel && (
        <span className="hidden shrink-0 text-[0.65rem] text-[var(--explore-text-muted)] sm:inline">
          {sourceLabel}
        </span>
      )}
      <span className="shrink-0 text-[0.65rem] text-[var(--explore-text-muted)] tabular-nums">
        {brief.timeAgo}
      </span>
    </button>
  );
}

// ─── Brief row: Flat (C) — title + desc + tags right ─────────

function FlatBriefRow({
  brief,
  surface,
  positionInList,
  onClick,
}: {
  brief: ExploreBriefCard;
  surface: BriefSurface;
  positionInList: number;
  onClick: () => void;
}) {
  const { ref, handleClick } = useBriefRowTracking({
    brief,
    surface,
    positionInList,
    onClick,
  });
  return (
    <button
      className="flex w-full gap-4 border-[var(--explore-border-light)] border-b py-3.5 text-left transition-colors last:border-b-0 hover:bg-[var(--explore-bg-highlight)]"
      onClick={handleClick}
      ref={ref}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[0.88rem] text-[var(--explore-text-primary)] leading-[1.4]">
          {brief.title}
        </p>
        {brief.tldr && (
          <p className="mt-1 line-clamp-1 text-[0.78rem] text-[var(--explore-text-secondary)] leading-[1.45]">
            {brief.tldr}
          </p>
        )}
      </div>
      {brief.tags.length > 0 && (
        <div className="hidden shrink-0 flex-col items-end gap-1 pt-1 sm:flex">
          {brief.tags.slice(0, 2).map((tag) => (
            <span
              className="rounded-full bg-[var(--explore-tag-bg)] px-2 py-0.5 text-[0.65rem] text-[var(--explore-tag-text)]"
              key={tag.slug}
            >
              {tag.displayName}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// ─── Brief row: Sidebar-card (D) — title + desc in card ──────

function SidebarCardBriefRow({
  brief,
  surface,
  positionInList,
  onClick,
}: {
  brief: ExploreBriefCard;
  surface: BriefSurface;
  positionInList: number;
  onClick: () => void;
}) {
  const { ref, handleClick } = useBriefRowTracking({
    brief,
    surface,
    positionInList,
    onClick,
  });
  return (
    <button
      className="flex w-full flex-col gap-1.5 px-4 py-3.5 text-left transition-colors hover:bg-[var(--explore-bg-highlight)]"
      onClick={handleClick}
      ref={ref}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      {/* Category badge — matches Explore sidebar variant */}
      {brief.categoryDisplayName && (
        <div className="flex items-center gap-1.5">
          <span className="text-[0.75rem]">{brief.categoryEmoji}</span>
          <span className="font-medium text-[0.68rem] text-[var(--explore-text-muted)]">
            {brief.categoryDisplayName}
          </span>
          <span className="text-[0.62rem] text-[var(--explore-text-muted)]">
            · {brief.timeAgo}
          </span>
        </div>
      )}
      <p className="font-semibold text-[0.88rem] text-[var(--explore-text-primary)] leading-[1.4]">
        {brief.title}
      </p>
      {brief.tldr && (
        <p className="line-clamp-2 text-[0.78rem] text-[var(--explore-text-secondary)] leading-[1.5]">
          {brief.tldr}
        </p>
      )}
    </button>
  );
}

// ─── Briefs list for a day section ───────────────────────────

function DayBriefsStyled({
  briefs,
  style,
  onBriefClick,
}: {
  briefs: ExploreBriefCard[];
  style: BriefCardStyle;
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  if (briefs.length === 0) {
    return null;
  }

  let surfaceForStyle: BriefSurface = "explore_briefs_compact";
  if (style === "flat") {
    surfaceForStyle = "explore_briefs_flat";
  } else if (style === "sidebar-card") {
    surfaceForStyle = "explore_briefs_sidebar";
  }

  // Flat style: no card wrapper
  if (style === "flat") {
    return (
      <div className="px-1 lg:px-0">
        {briefs.map((brief, idx) => (
          <FlatBriefRow
            brief={brief}
            key={brief.briefId}
            onClick={() => onBriefClick(brief)}
            positionInList={idx}
            surface={surfaceForStyle}
          />
        ))}
      </div>
    );
  }

  // All other styles: card wrapper with border
  const renderRow = (brief: ExploreBriefCard, idx: number) => {
    // biome-ignore lint/nursery/noUnnecessaryConditions: switch used for exhaustive style matching after early-return guard
    switch (style) {
      case "compact":
        return (
          <CompactBriefRow
            brief={brief}
            key={brief.briefId}
            onClick={() => onBriefClick(brief)}
            positionInList={idx}
            surface={surfaceForStyle}
          />
        );
      case "compact5":
        return (
          <Compact5BriefRow
            brief={brief}
            key={brief.briefId}
            onClick={() => onBriefClick(brief)}
            positionInList={idx}
            surface={surfaceForStyle}
          />
        );
      case "sidebar-card":
        return (
          <SidebarCardBriefRow
            brief={brief}
            key={brief.briefId}
            onClick={() => onBriefClick(brief)}
            positionInList={idx}
            surface={surfaceForStyle}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)]">
      <div className="divide-y divide-[var(--explore-border-light)]">
        {briefs.map(renderRow)}
      </div>
    </div>
  );
}

// ─── Day section ─────────────────────────────────────────────

function StyledDaySection({
  section,
  exploreBase,
  briefStyle,
  onBriefClick,
}: {
  section: FeedDaySection;
  exploreBase: string;
  briefStyle: BriefCardStyle;
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const hasStories = section.stories.length > 0;
  const hasBriefs = section.briefs.length > 0;

  if (!(hasStories || hasBriefs)) {
    return null;
  }

  return (
    <section className="flex flex-col gap-6">
      <div
        className="flex items-center gap-3 px-4 lg:px-0"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        <h2 className="font-semibold text-[0.85rem] text-[var(--explore-text-primary)]">
          {section.dateLabel}
        </h2>
        <div className="h-px flex-1 bg-[var(--explore-border-light)]" />
        {hasBriefs && (
          <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
            {section.briefs.length} brief
            {section.briefs.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {hasStories && (
        <div className="flex flex-col gap-3 px-1 lg:px-0">
          {section.stories.map((story) => (
            <FeaturedStoryCard
              exploreBase={exploreBase}
              key={story.storyId}
              story={story}
            />
          ))}
        </div>
      )}

      {hasBriefs && (
        <DayBriefsStyled
          briefs={section.briefs}
          onBriefClick={onBriefClick}
          style={briefStyle}
        />
      )}
    </section>
  );
}

// ─── Export ──────────────────────────────────────────────────

interface StyledFeedTimelineProps {
  data: FeedPageData;
  briefStyle: BriefCardStyle;
}

export function StyledFeedTimeline({
  data,
  briefStyle,
}: StyledFeedTimelineProps) {
  const { exploreBase } = useExploreRoute();
  const [selectedBrief, setSelectedBrief] = useState<ExploreBriefCard | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [storiesOpen, setStoriesOpen] = useState(false);

  const allBriefs = useMemo(
    () => data.sections.flatMap((s) => s.briefs),
    [data.sections]
  );
  const categoryBriefs = useMemo(() => {
    if (!selectedBrief) {
      return;
    }
    return allBriefs.filter(
      (brief) =>
        (brief.categorySlug ?? "uncategorized") ===
        (selectedBrief.categorySlug ?? "uncategorized")
    );
  }, [allBriefs, selectedBrief]);

  const handleBriefClick = useCallback((brief: ExploreBriefCard) => {
    setSelectedBrief(brief);
    setDialogOpen(true);
  }, []);
  const handleNavigateBrief = useCallback(
    (brief: ExploreBriefCard) => {
      handleBriefClick(brief);
    },
    [handleBriefClick]
  );

  return (
    <div className="flex flex-col gap-10">
      {data.sections.map((section) => (
        <StyledDaySection
          briefStyle={briefStyle}
          exploreBase={exploreBase}
          key={section.reportDate}
          onBriefClick={handleBriefClick}
          section={section}
        />
      ))}

      {data.sections.length === 0 && (
        <p
          className="py-12 text-center text-[0.9rem] text-[var(--explore-text-muted)]"
          style={{ fontFamily: "var(--homepage-sans-stack)" }}
        >
          No stories or briefs found.
        </p>
      )}

      <BriefPreviewDialog
        brief={selectedBrief}
        categoryBriefs={categoryBriefs}
        onNavigateBrief={handleNavigateBrief}
        onOpenChange={setDialogOpen}
        open={dialogOpen}
      />

      {storiesOpen && allBriefs.length > 0 && (
        <BriefStoriesOverlay
          briefs={allBriefs}
          onClose={() => setStoriesOpen(false)}
          startIndex={0}
        />
      )}
    </div>
  );
}
